import React from 'react';
import { AlertCircle } from 'lucide-react';

import { Alert, AlertDescription } from 'shadcn/components/ui/Alert';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';

/**
 * ManifestCard
 *
 * Reusable card wrapper for OOB and Frozen manifest sections.
 * Renders an icon + title header, a warning banner, summary stats, and a
 * children slot for the data table.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Icon element for the card header
 * @param {string} props.title - Card title (e.g., "OOB Manifest")
 * @param {string} props.warningText - Warning banner message
 * @param {Array<{label: string, value: string|number}>} props.stats - Summary stat items
 * @param {React.ReactNode} props.children - Table or content to render below stats
 */
function ManifestCard({ children, icon, stats, title, warningText }) {
    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                    {icon}
                    {title}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Warning Banner */}
                <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        <strong>Important:</strong> {warningText}
                    </AlertDescription>
                </Alert>

                {/* Summary Stats */}
                {stats && stats.length > 0 && (
                    <div className="grid grid-cols-3 gap-3">
                        {stats.map((stat) => (
                            <div key={stat.label} className="rounded-md border p-3">
                                <p className="text-xs text-muted-foreground">{stat.label}</p>
                                <p className="text-lg font-semibold">{stat.value}</p>
                            </div>
                        ))}
                    </div>
                )}

                {/* Table content */}
                {children}
            </CardContent>
        </Card>
    );
}

export default ManifestCard;
